/**
 * WCC360 v2 — Part 2 screens 5 through 13.
 *
 * One screen = one interaction = one bit of the discovery arc. Copy on
 * every screen is fixed by product — if you find yourself rewording it
 * to sound nicer, you are probably reintroducing the thing v1 got wrong
 * (a preference framed as a virtue with an obvious right answer).
 */
import { useMemo } from "react";
import { useAssessmentV2 } from "../store";
import { finishRun } from "../completion";
import { relabel, useScreenCopy } from "../copy";
import { bandParam } from "../band";


import { addSignal, type Archetype, type ArchetypeVector, emptyVector } from "../archetypes";
import type { ImpactDomain } from "@/lib/scoring";
import {
  emptySlots, writeMany, writeSlot, type SlotState,
} from "../slots";

import { CardSortRank, type RankItem } from "../components/interactions/CardSortRank";
import {
  ForcedChoiceRounds, bradleyTerry, type Round, type RoundPick,
} from "../components/interactions/ForcedChoiceRounds";
import { SwipeBlock, swipeToValue, type SwipeAnswer } from "../components/interactions/SwipeBlock";
import {
  FrequencyBlock, type FrequencyRow,
} from "../components/interactions/FrequencyBlock";
import { MultiSelect, type MultiSelectItem } from "../components/interactions/MultiSelect";
import { ScenarioChoice, type ScenarioOption } from "../components/interactions/ScenarioChoice";
import { CardRankTop2, type Top2Item } from "../components/interactions/CardRankTop2";
import { Slider } from "../components/interactions/Slider";
import { ChoiceScreen } from "../components/ChoiceScreen";

// -----------------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------------

/** Read (or seed) the running slot state from the store. */
function useSlots(): [SlotState, (next: SlotState) => void] {
  const { state, setPart2 } = useAssessmentV2();
  const slots = (state.part2.slots as SlotState) ?? emptySlots();
  return [slots, (next) => setPart2("slots", next)];
}

/** Read (or seed) the running archetype tally from the store. */
function useTally(): [ArchetypeVector, (next: ArchetypeVector) => void] {
  const { state, setPart2 } = useAssessmentV2();
  const tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
  return [tally, (next) => setPart2("archetype_tally", next)];
}

/**
 * Small consistent title block for every Part 2 screen.
 *
 * Pass `screenId` and the 15–18 band's restemmed wording is substituted
 * automatically (see copy.ts). The props here are always the 18+ default.
 */
function Head({
  screenId, eyebrow, title, subtitle,
}: { screenId?: string; eyebrow?: string; title: string; subtitle?: string }) {
  const c = useScreenCopy(screenId ?? "");
  eyebrow = eyebrow === undefined ? undefined : c.t("eyebrow", eyebrow);
  title = c.t("title", title);
  subtitle = subtitle === undefined ? undefined : c.t("subtitle", subtitle);

  return (
    <div className="mb-8">
      {eyebrow && (
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">{eyebrow}</div>
      )}
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">{title}</h1>
      {subtitle && <p className="mt-3 text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------
// 5 · W-SORT-001 — workday sort (CardSortRank)
// -----------------------------------------------------------------------

type SortId = "specialists" | "collab" | "solo" | "public" | "mobile" | "maker";

const SORT_ITEMS: RankItem[] = [
  { id: "specialists", label: "A small focused team of specialists" },
  { id: "collab",      label: "A busy collaborative floor" },
  { id: "solo",        label: "Mostly on my own, deep focus" },
  { id: "public",      label: "Out among clients, customers or community" },
  { id: "mobile",      label: "Moving between sites or locations" },
  { id: "maker",       label: "A quiet workshop, studio or lab" },
];

/** Per-card contribution to each slot. Undefined = card doesn't touch that slot. */
const SORT_MAP: Record<SortId, Partial<Record<"prefSoloVsTeam" | "prefOutdoor" | "prefMovement", number>>> = {
  solo:        { prefSoloVsTeam: 5 },
  specialists: { prefSoloVsTeam: 35 },
  maker:       { prefSoloVsTeam: 25, prefMovement: 55 },
  public:      { prefSoloVsTeam: 80, prefOutdoor: 60 },
  collab:      { prefSoloVsTeam: 90 },
  mobile:      { prefOutdoor: 70, prefMovement: 90 },
};

const RANK_WEIGHT = [1.0, 0.6, 0.3, 0, 0, 0];

export function WorkdaySortScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(ordered: string[]) {
    const bySlot: Record<string, { sum: number; w: number }> = {};
    ordered.forEach((id, rank) => {
      const w = RANK_WEIGHT[rank] ?? 0;
      if (w === 0) return;
      const map = SORT_MAP[id as SortId] ?? {};
      for (const [slot, val] of Object.entries(map)) {
        const acc = bySlot[slot] ?? { sum: 0, w: 0 };
        acc.sum += val! * w;
        acc.w += w;
        bySlot[slot] = acc;
      }
    });
    const writes: Record<string, number> = {};
    for (const [slot, { sum, w }] of Object.entries(bySlot)) {
      writes[slot] = Math.round(sum / w);
    }

    // tolSocialLoad from the top 2 vs bottom 2 pattern.
    const highContact: SortId[] = ["collab", "public"];
    const top2 = ordered.slice(0, 2).filter((x) => highContact.includes(x as SortId)).length;
    const bot2 = ordered.slice(-2).filter((x) => highContact.includes(x as SortId)).length;
    let social = 50;
    if (top2 === 2) social = 90;
    else if (top2 === 1) social = 65;
    else if (bot2 === 2) social = 20;

    let next = writeMany(slots, "W-SORT-001", {
      ...writes,
    } as never, { label: "Ranked workday sort" });
    next = writeSlot(next, "tolSocialLoad", "W-SORT-001", social, {
      label: "Ranked workday sort — social pattern",
    });
    setSlots(next);
    setPart2("p2_workday_sort", ordered);
    setTimeout(onNext, 240);
  }

  // Band restems relabel the six cards; ids, slot map and rank weights are
  // never touched, so the same drag order scores identically in every band.
  const c = useScreenCopy("p2_workday_sort");
  const items = relabel(SORT_ITEMS, c);

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_workday_sort"
        eyebrow="Where you'd want your hours to go"
        title="Drag these workdays into your order — the one you'd most want at the top."
        subtitle="It's fine to like more than one. Rank them anyway."
      />
      <CardSortRank items={items} onComplete={submit} />
    </div>
  );
}



// -----------------------------------------------------------------------
// 6 · W-OFFER-001 — two offers, 4 rounds (ForcedChoiceRounds)
// -----------------------------------------------------------------------

import type { RoundOffer } from "../components/interactions/ForcedChoiceRounds";
const OFFERS: Record<string, RoundOffer> = {
  income:      { id: "income",      title: "Much higher pay, work you'd find ordinary", tags: ["income+"] },
  loveLowPay:  { id: "loveLowPay",  title: "Less pay, work you'd genuinely love",       tags: ["income-", "impact+"] },
  autonomy:    { id: "autonomy",    title: "Total freedom over how and when you work",  tags: ["autonomy+"] },
  prestige:    { id: "prestige",    title: "A name everyone recognises on your CV — the record of where you've worked", tags: ["prestige+"] },
  stability:   { id: "stability",   title: "Guaranteed security for the next 10 years",  tags: ["stability+"] },
  impact:      { id: "impact",      title: "Work where you see the people you helped",   tags: ["impact+"] },
};


const OFFER_ROUNDS: Round[] = [
  { id: "r1", left: OFFERS.income,    right: OFFERS.loveLowPay },
  { id: "r2", left: OFFERS.autonomy,  right: OFFERS.stability },
  { id: "r3", left: OFFERS.prestige,  right: OFFERS.impact },
  { id: "r4", left: OFFERS.autonomy,  right: OFFERS.income },
];

const OFFER_TAGS = ["income+", "autonomy+", "prestige+", "stability+", "impact+"];

export function TwoOffersScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(picks: RoundPick[]) {
    const bt = bradleyTerry(picks, OFFER_TAGS);
    const next = writeMany(slots, "W-OFFER-001", {
      prefIncomeUpside:     bt["income+"],
      prefAutonomy:         bt["autonomy+"],
      prefPrestige:         bt["prestige+"],
      prefStability:        bt["stability+"],
      prefImpactVisibility: bt["impact+"],
    }, { label: "Trade-off tournament" });
    setSlots(next);
    setPart2("p2_two_offers", picks);
    setTimeout(onNext, 260);
  }

  // Relative-money restem for 15–18: same offers, same tags, same tournament
  // — a teenager has no anchor for an LPA figure, so the units become
  // "much more" and "less" rather than a number.
  const c = useScreenCopy("p2_two_offers");
  const rounds: Round[] = OFFER_ROUNDS.map((r) => ({
    ...r,
    left: { ...r.left, title: c.opt(r.left.id, r.left.title) },
    right: { ...r.right, title: c.opt(r.right.id, r.right.title) },
  }));

  return (
    <div className="max-w-3xl mx-auto">
      <Head
        screenId="p2_two_offers"
        eyebrow="Same start date"
        title="Two offers. Same start date. Which do you take?"
        subtitle="Not which is more sensible. Which one, if you had to sign tomorrow."
      />
      <ForcedChoiceRounds rounds={rounds} onComplete={submit} />

    </div>
  );
}

// -----------------------------------------------------------------------
// 6.5 · W-TRADE-002 — three trade pairs (ForcedChoiceRounds)
// -----------------------------------------------------------------------

const TRADE_ROUNDS: Round[] = [
  {
    id: "t1",
    left:  { id: "no_check",  title: "Nobody checks how you do it",             tags: ["autonomy+"] },
    right: { id: "daily",     title: "A manager who reviews your work every day", tags: ["autonomy-"] },
  },
  {
    id: "t2",
    left:  { id: "two_yr",    title: "A two-year contract, 30% more money",     tags: ["stability-"] },
    right: { id: "permanent", title: "A permanent job on your current money",   tags: ["stability+"] },
  },

  {
    id: "t3",
    left:  { id: "method",    title: "A proven method to follow",                tags: ["creativity-"] },
    right: { id: "your_way",  title: "You work out how it should be done",       tags: ["creativity+"] },
  },
];

const TRADE_TAGS = ["autonomy+", "autonomy-", "stability+", "stability-", "creativity+", "creativity-"];

export function TradeOffsScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(picks: RoundPick[]) {
    const bt = bradleyTerry(picks, TRADE_TAGS);
    // "autonomy" ranges 0..100 by (autonomy+ − autonomy-)/2 + 50 style;
    // simpler: use the +tag score directly since only one round touches each.
    const autonomy   = picks[0].chosenId === "no_check"  ? 100 : 0;
    const stability  = picks[1].chosenId === "permanent" ? 100 : 0;
    const creativity = picks[2].chosenId === "your_way"  ? 100 : 0;

    // Blend with W-OFFER-001 tournament: 50/50 mean. writeSlot's built-in
    // conflict resolution will flip these to low-confidence if OFFER and
    // TRADE disagree by more than the threshold.
    const next = writeMany(slots, "W-TRADE-002", {
      prefAutonomy:   autonomy,
      prefStability:  stability,
      prefCreativity: creativity,
    }, { label: "Same-job trade-offs", disagreeThreshold: 50 });
    void bt;
    setSlots(next);
    setPart2("p2_trade_offs", picks);
    setTimeout(onNext, 260);
  }

  // 15–18 restem: the two-year contract pair means nothing before your
  // first job. Same ids, same stability 100/0 split, framed as longevity.
  const c = useScreenCopy("p2_trade_offs");
  const rounds: Round[] = TRADE_ROUNDS.map((r) => ({
    ...r,
    left: { ...r.left, title: c.opt(r.left.id, r.left.title) },
    right: { ...r.right, title: c.opt(r.right.id, r.right.title) },
  }));

  return (
    <div className="max-w-3xl mx-auto">
      <Head
        screenId="p2_trade_offs"
        eyebrow="Three more"
        title="Same job, same pay — which version do you take?"
      />
      <ForcedChoiceRounds
        rounds={rounds}

        onComplete={submit}
        interstitial={(i, total) => `Pair ${i + 1} of ${total}`}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 7 · W-BODY-001 — quick ones (SwipeBlock)
// -----------------------------------------------------------------------

// Neutral concrete examples (B8). b1, b3 and b5 get one. SKIPPED on
// purpose, because no example is neutral for them:
//   b2 (movement while working) — every job we could name reads as an
//       endorsement of one side.
//   b4 (frequent travel) — travel examples are either aspirational
//       (pilot, consultant) or off-putting (long-haul trucking).
//   b6 (blood / physical distress) — every honest example is clinical
//       and pushes the answer towards "no".
const BODY_PROMPTS: {
  id: string; text: string; example?: string;
  slot: "prefOutdoor" | "prefMovement" | "prefScreenTime" | "prefTravel" | "tolClosePhysical" | "tolSensory";
}[] = [
  { id: "b1", slot: "prefOutdoor",       text: "I'd choose work that keeps me outdoors or moving over a desk job", example: "like a gardener or a site supervisor" },
  { id: "b2", slot: "prefMovement",      text: "I work best when I can move around rather than stay seated all day" },
  { id: "b3", slot: "prefScreenTime",    text: "I'm comfortable spending most of my workday on a screen", example: "like an accountant or a graphic designer" },
  { id: "b4", slot: "prefTravel",        text: "I'd welcome work that means travelling often" },
  { id: "b5", slot: "tolClosePhysical",  text: "I'm comfortable with hands-on work that means close physical contact with people", example: "like a dentist or a physiotherapist" },
  { id: "b6", slot: "tolSensory",        text: "I stay calm and focused around blood or someone in physical distress" },
];


export function QuickOnesScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(answers: Record<string, SwipeAnswer>) {
    const writes: Record<string, number> = {};
    for (const p of BODY_PROMPTS) {
      const a = answers[p.id];
      if (a) writes[p.slot] = swipeToValue(a);
    }
    const next = writeMany(slots, "W-BODY-001", writes as never, { label: "Quick body/work-mode swipe" });
    setSlots(next);
    setPart2("p2_quick_ones", answers);
    setTimeout(onNext, 200);
  }

  // Restems swap the prompt wording only. Prompt id → slot is fixed.
  const c = useScreenCopy("p2_quick_ones");

  return (
    <div className="max-w-xl mx-auto">
      <Head screenId="p2_quick_ones" eyebrow="Six quick ones" title="No thinking. First reaction." />
      <SwipeBlock
        prompts={BODY_PROMPTS.map(({ id, text, example }) => ({
          id,
          text: c.opt(id, text),
          example: c.ex(id, example),
        }))}
        onComplete={submit}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 7.5 · W-REALITY-001 — last month (FrequencyBlock)
//
// The corroboration engine. Each row is paired with a slot the learner
// already touched in BODY. If revealed value differs from stated by more
// than 35, the slot flips to low-confidence and keeps the REVEALED value.
// -----------------------------------------------------------------------

const REALITY_ROWS: (FrequencyRow & {
  slot: "prefScreenTime" | "tolEmotionalLoad" | "tolPublicSpeaking" | "prefTravel"
       | "prefMovement" | "prefFocusVsSwitching" | "prefStableSchedule";
})[] = [
  {
    id: "r_screen", slot: "prefScreenTime",
    question: "Hours a day you were already on a screen",
    levels: [
      { id: "u2",  label: "Under 2",  value: 10 },
      { id: "24",  label: "2–4",     value: 40 },
      { id: "47",  label: "4–7",     value: 75 },
      { id: "7p",  label: "7+",       value: 100 },
    ],
  },
  {
    id: "r_stress", slot: "tolEmotionalLoad",
    question: "Days work or study stress followed you home",
    levels: [
      { id: "none",   label: "Almost none", value: 100 },
      { id: "few",    label: "A few",       value: 70 },
      { id: "half",   label: "About half",  value: 35 },
      { id: "most",   label: "Most days",   value: 10 },
    ],
  },
  {
    id: "r_speak", slot: "tolPublicSpeaking",
    question: "Times you spoke to a group of ten or more",
    levels: [
      { id: "never", label: "Never",     value: 15 },
      { id: "once",  label: "Once",      value: 45 },
      { id: "25",    label: "2–5",       value: 75 },
      { id: "5p",    label: "More than 5", value: 100 },
    ],
  },
  {
    id: "r_travel", slot: "prefTravel",
    question: "Nights away from home for work or study",
    levels: [
      { id: "none", label: "None", value: 10 },
      { id: "12",   label: "1–2",  value: 40 },
      { id: "37",   label: "3–7",  value: 70 },
      { id: "8p",   label: "8+",    value: 100 },
    ],
  },
  {
    id: "r_sit", slot: "prefMovement",
    question: "Share of the day you were sitting down",
    levels: [
      { id: "most", label: "Most",       value: 10 },
      { id: "half", label: "About half", value: 45 },
      { id: "not",  label: "Not much",   value: 75 },
      { id: "barely", label: "Barely any", value: 100 },
    ],
  },
  {
    id: "r_switch", slot: "prefFocusVsSwitching",
    question: "Times a day your work got interrupted",
    levels: [
      { id: "barely", label: "Barely ever", value: 10 },
      { id: "few",    label: "A few",       value: 40 },
      { id: "const",  label: "Constantly",  value: 75 },
      { id: "never_clear", label: "Never a clear run", value: 95 },
    ],
  },
  {
    id: "r_evening", slot: "prefStableSchedule",
    question: "Evenings or weekends you had to work",
    levels: [
      { id: "none", label: "None",         value: 95 },
      { id: "12",   label: "1–2",           value: 70 },
      { id: "36",   label: "3–6",           value: 35 },
      { id: "most", label: "Most weeks",   value: 10 },
    ],
  },
];

export function LastMonthScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(answers: Record<string, { levelId: string; value: number }>) {
    // Each row writes its slot with threshold 35 — matching the spec.
    let next = slots;
    for (const row of REALITY_ROWS) {
      const a = answers[row.id];
      if (!a) continue;
      next = writeSlot(next, row.slot, "W-REALITY-001", a.value, {
        label: `Last month: ${row.question.toLowerCase()}`,
        disagreeThreshold: 35,
      });
    }
    setSlots(next);
    setPart2("p2_last_month", answers);
    setTimeout(onNext, 240);
  }

  // 15–18 restem: same seven rows, same slots, same values, school-framed.
  // This is the corroboration engine — the rows must keep pairing with the
  // BODY slots they check, so only `question` is ever substituted.
  const c = useScreenCopy("p2_last_month");
  const rows = REALITY_ROWS.map((r) => ({ ...r, question: c.row(r.id, r.question) }));

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_last_month"
        eyebrow="Behaviour, not intention"
        title="Thinking about the last month — not how you'd like things to be."
        subtitle="What you actually did last month beats what you're comfortable with in principle."
      />
      <FrequencyBlock rows={rows} onComplete={submit} />

    </div>
  );
}

// -----------------------------------------------------------------------
// 8 · W-GRIND-001 — grind test (MultiSelect)
// -----------------------------------------------------------------------

const GRIND_ITEMS: MultiSelectItem[] = [
  { id: "admin",     label: "Detailed paperwork and admin" },
  { id: "physical",  label: "Repetitive hands-on or physical tasks" },
  { id: "difficult", label: "Difficult people, complaints or rejection" },
  { id: "solo_slow", label: "Long solo work with slow results" },
  { id: "deadlines", label: "Working to tight deadlines" },
  { id: "on_feet",   label: "Being on your feet all day" },
];

export function GrindTestScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function submit(picked: string[]) {
    const value = Math.round((picked.length / GRIND_ITEMS.length) * 100);
    const next = writeSlot(slots, "tolGruntWork", "W-GRIND-001", value, {
      label: `${picked.length} of 6 grind items tolerated`,
    });
    setSlots(next);
    // Store per-facet flags separately — a later engine version filters on
    // these individually, and they are not interchangeable.
    setPart2("p2_grind_test", { picked, facets: Object.fromEntries(GRIND_ITEMS.map((it) => [it.id, picked.includes(it.id)])) });
    setTimeout(onNext, 220);
  }

  const c = useScreenCopy("p2_grind_test");
  const items = relabel(GRIND_ITEMS, c);

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_grind_test"

        eyebrow="Every job has a boring bit"
        title="Every job has parts that are boring or hard. Which could you tolerate repeatedly?"
        subtitle="Not enjoy — tolerate. Zero is a legitimate answer."
      />
      <MultiSelect items={items} onComplete={submit} />
    </div>
  );
}

// -----------------------------------------------------------------------
// 8.5 · W-CLINICAL-001 — collapse in front of you (ScenarioChoice)
//
// Corroborates W-BODY-001's stated tolerances against a specific,
// concrete situation. Take the MIN — under-tolerance is a welfare risk.
// -----------------------------------------------------------------------

type ClinicalValues = { sensory: number; physical: number };

const CLINICAL_OPTIONS: ScenarioOption<ClinicalValues>[] = [
  { id: "straight_in", label: "I'd get straight in and do what's needed", values: { sensory: 100, physical: 100 } },
  { id: "feel_but_help", label: "I'd feel it, but I'd still help",         values: { sensory: 75,  physical: 80 } },
  { id: "call_and_talk", label: "I'd call for help and keep them talking", values: { sensory: 45,  physical: 35 } },
  { id: "struggle",      label: "I'd struggle — I'd need someone else to take over", values: { sensory: 10, physical: 10 } },
];

export function CollapseScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function pick(opt: ScenarioOption<ClinicalValues>) {
    // forceConservative — take the lower of stated (W-BODY) and revealed (this).
    let next = writeSlot(slots, "tolSensory", "W-CLINICAL-001", opt.values.sensory, {
      label: "Collapse scenario", forceConservative: true, disagreeThreshold: 40,
    });
    next = writeSlot(next, "tolClosePhysical", "W-CLINICAL-001", opt.values.physical, {
      label: "Collapse scenario", forceConservative: true, disagreeThreshold: 40,
    });
    setSlots(next);
    setPart2("p2_collapse", opt.id);
    setTimeout(onNext, 260);
  }

  // 12–14 restem: same two slots, same conservative MIN, same four option
  // values. The scene moves to a playground fall — clinical body-fluid
  // imagery has no place in front of a twelve-year-old, and calm around
  // another person's distress is the same tolerance either way.
  const c = useScreenCopy("p2_collapse");
  const options = CLINICAL_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }));

  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_collapse" eyebrow="One quick scene" title="Someone collapses in front of you." />
      <ScenarioChoice
        situation={c.t("situation", "There's blood. Help is four minutes away. What happens to you?")}
        options={options}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 9 · W-BADDAY-001 — bad day (CardRankTop2)
// -----------------------------------------------------------------------

const BADDAY_ITEMS: (Top2Item & { archetype: Partial<Record<Archetype, number>> })[] = [
  { id: "help",   label: "Helping or fixing something for someone", archetype: { Caregiver: 1 } },
  { id: "puzzle", label: "Solving a hard puzzle",                    archetype: { Thinker: 1 } },
  { id: "make",   label: "Making something beautiful or original",   archetype: { Creator: 1 } },
  // "Winning, or hitting a goal" → Leader 0.6 (entrepreneurial energy folds into Leader).
  { id: "win",    label: "Winning, or hitting a goal",               archetype: { Leader: 1 } },
  { id: "build",  label: "Building something that lasts",            archetype: { Builder: 1 } },
];

export function BadDayScreen({ onNext }: { onNext: () => void }) {
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();

  function submit(first: string, second: string) {
    let next = tally;
    const f = BADDAY_ITEMS.find((x) => x.id === first)?.archetype ?? {};
    const s = BADDAY_ITEMS.find((x) => x.id === second)?.archetype ?? {};
    next = addSignal(next, f, 1.0);
    next = addSignal(next, s, 0.5);
    setTally(next);
    setPart2("p2_bad_day", { first, second });
    setTimeout(onNext, 260);
  }

  const c = useScreenCopy("p2_bad_day");
  const items = relabel(BADDAY_ITEMS, c);

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_bad_day"
        eyebrow="Nobody's watching"
        title="What would you still find worth doing on a bad day, when no one is watching?"
        subtitle="Pick the one that fits first, then the runner-up."
      />
      <CardRankTop2 items={items} onComplete={submit} />
    </div>
  );
}

// -----------------------------------------------------------------------
// 10 · W-PROBLEM-001 — whose problem (CardRankTop2)
// Sets impactDomain1 / impactDomain2 — 20% of the eventual match score.
// -----------------------------------------------------------------------

const PROBLEM_ITEMS: (Top2Item & { domain: ImpactDomain; archetype?: Partial<Record<Archetype, number>> })[] = [
  { id: "care",    label: "People who are unwell or in need of care",                domain: "care",    archetype: { Caregiver: 1 } },
  { id: "grow",    label: "People and businesses trying to build and grow",          domain: "grow" },
  { id: "planet",  label: "The planet and the future",                                 domain: "planet",  archetype: { Advocate: 1 } },
  { id: "learn",   label: "People who want to learn, or to be moved by something",   domain: "learn" },
  { id: "systems", label: "Systems and communities that need to work better",         domain: "systems", archetype: { Organizer: 1 } },
];

export function WhoseProblemScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();

  function submit(first: string, second: string) {
    const f = PROBLEM_ITEMS.find((x) => x.id === first)!;
    const s = PROBLEM_ITEMS.find((x) => x.id === second)!;
    let nextTally = tally;
    if (f.archetype) nextTally = addSignal(nextTally, f.archetype, 0.6);
    if (s.archetype) nextTally = addSignal(nextTally, s.archetype, 0.3);
    setTally(nextTally);

    // Impact visibility: care/planet first → 85, else 55.
    const impactVis = f.domain === "care" || f.domain === "planet" ? 85 : 55;
    const nextSlots = writeSlot(slots, "prefImpactVisibility", "W-PROBLEM-001", impactVis, {
      label: `First-choice domain: ${f.domain}`,
    });
    setSlots(nextSlots);

    setPart2("p2_whose_problem", { first: f.domain, second: s.domain });
    setTimeout(onNext, 260);
  }

  const c = useScreenCopy("p2_whose_problem");
  const items = relabel(PROBLEM_ITEMS, c);

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_whose_problem"
        eyebrow="The people you'd show up for"
        title="Whose problem would you most want to be the one to solve?"
        subtitle="First choice matters most — it sets what work you'll be pointed at."
      />
      <CardRankTop2 items={items} onComplete={submit} labels={{ first: "First", second: "Then" }} />
    </div>
  );
}

// -----------------------------------------------------------------------
// 11 · W-ROOM-001 — which team (single choice, nominal tag)
// -----------------------------------------------------------------------

type RoomTag = "calm" | "fast" | "clear" | "warm";
const ROOM_CHOICES = [
  { value: "calm"  as RoomTag, label: "Calm and supportive, people come first" },
  { value: "fast"  as RoomTag, label: "Fast and competitive, everyone is ambitious" },
  { value: "clear" as RoomTag, label: "Clear and organised, everyone knows the plan" },
  { value: "warm"  as RoomTag, label: "Warm and informal, everyone mucks in" },
];

export function RoomScreen({ onNext }: { onNext: () => void }) {
  const { state, setPart2 } = useAssessmentV2();
  const current = state.part2["p2_room"] as RoomTag | undefined;
  const c = useScreenCopy("p2_room");

  return (
    <div className="max-w-2xl mx-auto">
      <ChoiceScreen<RoomTag>
        title={c.t("title", "Which team would you most want to join?")}
        subtitle={c.t("subtitle", "This one isn't scored — it only changes how we explain your matches later.")}
        choices={c.choices(ROOM_CHOICES)}
        value={current}
        onSelect={(v) => setPart2("p2_room", v)}
        onContinue={onNext}
      />

    </div>
  );
}

// -----------------------------------------------------------------------
// 12 · W-FOCUS-001 — focus vs switching (Slider)
// -----------------------------------------------------------------------

export function FocusSliderScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function commit(v: number) {
    const next = writeSlot(slots, "prefFocusVsSwitching", "W-FOCUS-001", v, {
      label: "Focus-vs-switching slider",
    });
    setSlots(next);
    setPart2("p2_focus_slider", v);
    setTimeout(onNext, 240);
  }

  const c = useScreenCopy("p2_focus_slider");
  return (
    <div className="max-w-xl mx-auto">
      <Head screenId="p2_focus_slider" eyebrow="How your brain likes to work" title="Deep focus on one thing, or juggling several?" />
      <Slider
        leftLabel={c.t("leftLabel", "One thing at a time")}
        rightLabel={c.t("rightLabel", "Lots on the go")}
        onCommit={commit}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 13 · W-BATTERY-001 — people battery (Slider)
// -----------------------------------------------------------------------

export function PeopleBatteryScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();

  function commit(v: number) {
    const next = writeSlot(slots, "tolSocialLoad", "W-BATTERY-001", v, {
      label: "After a full people day",
    });
    setSlots(next);
    setPart2("p2_people_battery", v);
    setTimeout(onNext, 240);
  }

  const c = useScreenCopy("p2_people_battery");
  return (
    <div className="max-w-xl mx-auto">
      <Head
        screenId="p2_people_battery"

        eyebrow="Your energy, honestly"
        title="After a full day of constant people contact, are you charged up or drained?"
      />
      <Slider
        leftLabel={c.t("leftLabel", "Completely drained")}
        rightLabel={c.t("rightLabel", "Charged up")}
        onCommit={commit}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 14 · W-HARDDAY-001 — hard day (ScenarioChoice)
// Writes tolEmotionalLoad, tolSocialLoad, tolPeopleContact.
// -----------------------------------------------------------------------

type HardDayValues = { emotional: number; social: number; contact: number };

const HARDDAY_OPTIONS: ScenarioOption<HardDayValues>[] = [
  { id: "people",  label: "Find my people — talk it out with someone I trust",
    values: { emotional: 70, social: 85, contact: 80 } },
  { id: "move",    label: "Get moving — walk, gym, drive it off",
    values: { emotional: 60, social: 40, contact: 45 } },
  { id: "quiet",   label: "Straight home. Silence, and I need it.",
    values: { emotional: 30, social: 15, contact: 25 } },
  { id: "outwork", label: "Get back to work and out-work it",
    values: { emotional: 90, social: 55, contact: 60 } },
];

export function HardDayScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<HardDayValues>) {
    let next = writeSlot(slots, "tolEmotionalLoad", "W-HARDDAY-001", opt.values.emotional,
      { label: "After a hard day", disagreeThreshold: 35 });
    next = writeSlot(next, "tolSocialLoad", "W-HARDDAY-001", opt.values.social,
      { label: "After a hard day", disagreeThreshold: 35 });
    next = writeSlot(next, "tolPeopleContact", "W-HARDDAY-001", opt.values.contact,
      { label: "After a hard day" });
    setSlots(next);
    setPart2("p2_hard_day", opt.id);
    setTimeout(onNext, 260);
  }
  // 15–18 restem: same four options, same three slots, same values. Work
  // stress becomes the thing that actually loads a teenager — carrying
  // someone else's hard day.
  const c = useScreenCopy("p2_hard_day");
  const options = HARDDAY_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }));

  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_hard_day" eyebrow="How you recover" title="You've just had a genuinely rough day." />
      <ScenarioChoice
        situation={c.t("situation", "Something at work went wrong and it stuck. It's evening. What do you actually do?")}
        options={options}

        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 15 · W-SCHED-001 — working pattern (single choice → prefStableSchedule)
// -----------------------------------------------------------------------

type SchedTag = "regular" | "mostly" | "bursts" | "onDemand";
const SCHED_MAP: Record<SchedTag, number> = { regular: 95, mostly: 70, bursts: 35, onDemand: 10 };

export function WorkingPatternScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { state, setPart2 } = useAssessmentV2();
  const current = state.part2["p2_working_pattern"] as SchedTag | undefined;

  const choices: { value: SchedTag; label: string }[] = [
    { value: "regular",  label: "Same hours every day — out by 6, evenings mine" },
    { value: "mostly",   label: "Mostly regular, a few late nights when it counts" },
    { value: "bursts",   label: "Project bursts — flat weeks, then heavy ones" },
    { value: "onDemand", label: "Whatever the work needs, whenever it needs it" },
  ];

  function pick(v: SchedTag) {
    const next = writeSlot(slots, "prefStableSchedule", "W-SCHED-001", SCHED_MAP[v], {
      label: "Working pattern preferred",
      disagreeThreshold: 35,
    });
    setSlots(next);
    setPart2("p2_working_pattern", v);
  }

  const c = useScreenCopy("p2_working_pattern");

  return (
    <div className="max-w-2xl mx-auto">
      <ChoiceScreen<SchedTag>
        title={c.t("title", "How would you want your working week to run?")}
        subtitle={c.t("subtitle", "Not what your current job is. What you'd choose.")}
        choices={c.choices(choices)}
        value={current}
        onSelect={pick}
        onContinue={onNext}
      />

    </div>
  );
}

// -----------------------------------------------------------------------
// 16 · W-SPEAK-001 — presenting (Slider → tolPublicSpeaking)
// -----------------------------------------------------------------------

export function PresentingScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();
  function commit(v: number) {
    const next = writeSlot(slots, "tolPublicSpeaking", "W-SPEAK-001", v, {
      label: "Presenting comfort", disagreeThreshold: 35,
    });
    setSlots(next);
    setPart2("p2_presenting", v);
    setTimeout(onNext, 240);
  }
  const c = useScreenCopy("p2_presenting");
  return (
    <div className="max-w-xl mx-auto">
      <Head
        screenId="p2_presenting"
        eyebrow="Standing up to talk"
        title="One-to-one, or a room full of strangers?"
        subtitle="How you'd feel about speaking, honestly — not how you think you should feel."
      />
      <Slider
        leftLabel={c.t("leftLabel", "A quiet 1-to-1 is my speed")}
        rightLabel={c.t("rightLabel", "A room of 200 is fine")}
        onCommit={commit}
      />

    </div>
  );
}

// -----------------------------------------------------------------------
// 17 · W-TENYEAR-001 — ten-year self-portrait (CardRankTop2 → archetype)
// -----------------------------------------------------------------------

const TENYEAR_ITEMS: (Top2Item & { archetype: Partial<Record<Archetype, number>> })[] = [
  { id: "built",   label: "Built something people can point to",         archetype: { Builder: 1 } },
  { id: "cracked", label: "Solved a problem people said was unsolvable", archetype: { Thinker: 1 } },
  { id: "moved",   label: "Made something that moved people",            archetype: { Creator: 1 } },
  { id: "lifted",  label: "Known for lifting the people around me",      archetype: { Caregiver: 1 } },
  { id: "led",     label: "In the room where the decisions get made",    archetype: { Leader: 1 } },
  { id: "changed", label: "Changed how something in the world runs",     archetype: { Advocate: 1 } },
];

export function TenYearsScreen({ onNext }: { onNext: () => void }) {
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();
  function submit(first: string, second: string) {
    const f = TENYEAR_ITEMS.find((x) => x.id === first)?.archetype ?? {};
    const s = TENYEAR_ITEMS.find((x) => x.id === second)?.archetype ?? {};
    let next = addSignal(tally, f, 1.2);
    next = addSignal(next, s, 0.6);
    setTally(next);
    setPart2("p2_ten_years", { first, second });
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_ten_years");
  const items = relabel(TENYEAR_ITEMS, c);
  return (
    <div className="max-w-2xl mx-auto">
      <Head
        screenId="p2_ten_years"
        eyebrow="Ten years from now"
        title="If ten years from now you looked back proud, which of these got you there?"
        subtitle="Pick the one that fits first, then the runner-up."
      />
      <CardRankTop2 items={items} onComplete={submit} />
    </div>
  );
}

// -----------------------------------------------------------------------
// 18 · W-WRONG-001 — 9pm deadline scenario
// Writes tolEmotionalLoad + tolGruntWork.
// -----------------------------------------------------------------------

type WrongValues = { emotional: number; grunt: number };
const WRONG_OPTIONS: ScenarioOption<WrongValues>[] = [
  { id: "stay",   label: "I stay. It gets fixed tonight.",                       values: { emotional: 90, grunt: 85 } },
  { id: "plan",   label: "I help sketch a plan, then early start tomorrow.",     values: { emotional: 65, grunt: 55 } },
  { id: "flag",   label: "I flag it, but this isn't mine to carry.",             values: { emotional: 45, grunt: 25 } },
  { id: "leave",  label: "Someone else's error, someone else's night.",          values: { emotional: 25, grunt: 10 } },
];

export function ScenarioDeadlineScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<WrongValues>) {
    let next = writeSlot(slots, "tolEmotionalLoad", "W-WRONG-001", opt.values.emotional,
      { label: "Late-night deadline", disagreeThreshold: 40 });
    next = writeSlot(next, "tolGruntWork", "W-WRONG-001", opt.values.grunt,
      { label: "Late-night deadline", disagreeThreshold: 40 });
    setSlots(next);
    setPart2("p2_scenario_deadline", opt.id);
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_scenario_deadline");
  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_scenario_deadline" eyebrow="A Thursday, 9pm" title="A senior colleague finds a real error in the deck they need at 9am." />
      <ScenarioChoice
        situation={c.t("situation", "Everyone else has left. It's fixable, but it's hours. What actually happens?")}
        options={WRONG_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }))}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 19 · W-MONTH-001 — a month off (Scenario → archetype)
// -----------------------------------------------------------------------

type MonthValues = { archetype: Partial<Record<Archetype, number>> };
const MONTH_OPTIONS: ScenarioOption<MonthValues>[] = [
  { id: "build",     label: "Build or fix something I've been putting off",   values: { archetype: { Builder: 1 } } },
  { id: "learn",     label: "Deep-dive a subject I've been circling",         values: { archetype: { Thinker: 1 } } },
  { id: "make",      label: "Make something — writing, film, music, design",  values: { archetype: { Creator: 1 } } },
  { id: "travel",    label: "Travel — new places, new people",                values: { archetype: { Connector: 1 } } },
  { id: "volunteer", label: "Volunteer somewhere that matters to me",         values: { archetype: { Advocate: 0.6, Caregiver: 0.6 } } },
];

export function ScenarioMonthOffScreen({ onNext }: { onNext: () => void }) {
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<MonthValues>) {
    const next = addSignal(tally, opt.values.archetype, 0.8);
    setTally(next);
    setPart2("p2_scenario_month_off", opt.id);
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_scenario_month_off");
  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_scenario_month_off" eyebrow="A whole month" title="A month off, fully paid. Nobody expects anything." />
      <ScenarioChoice
        situation={c.t("situation", "What honestly ends up filling it? First instinct.")}
        options={MONTH_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }))}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 20 · W-ROCKET-001 — public failure (Scenario → archetype + tolEmotionalLoad)
// -----------------------------------------------------------------------

type RocketValues = { archetype: Partial<Record<Archetype, number>>; emotional: number };
const ROCKET_OPTIONS: ScenarioOption<RocketValues>[] = [
  { id: "next",      label: "Running the next attempt — sharper this time",         values: { archetype: { Leader: 0.6, Builder: 0.6 }, emotional: 85 } },
  { id: "under",     label: "Working under someone I trust, learning fast",         values: { archetype: { Thinker: 0.7 },              emotional: 60 } },
  { id: "stable",    label: "In a stable role while I decide what's next",          values: { archetype: {},                             emotional: 40 } },
  { id: "different", label: "Doing something completely different",                 values: { archetype: { Creator: 0.6, Advocate: 0.4 }, emotional: 30 } },
];

export function ScenarioFailureScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<RocketValues>) {
    const nextTally = addSignal(tally, opt.values.archetype, 0.6);
    setTally(nextTally);
    const nextSlots = writeSlot(slots, "tolEmotionalLoad", "W-ROCKET-001", opt.values.emotional, {
      label: "Six months after a public failure",
      disagreeThreshold: 40,
    });
    setSlots(nextSlots);
    setPart2("p2_scenario_failure", opt.id);
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_scenario_failure");
  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_scenario_failure" eyebrow="Six months later" title="A project you led just failed. Publicly." />
      <ScenarioChoice
        situation={c.t("situation", "Money and time gone. Six months later — where are you, honestly?")}
        options={ROCKET_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }))}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 21 · W-STRUGGLE-001 — struggling teammate (Scenario → Caregiver/Connector/Leader)
// -----------------------------------------------------------------------

const STRUGGLE_OPTIONS: ScenarioOption<{ archetype: Partial<Record<Archetype, number>> }>[] = [
  { id: "aside",   label: "Pull them aside — see what's actually going on",    values: { archetype: { Caregiver: 1 } } },
  { id: "cover",   label: "Quietly cover for them until they're back on it",   values: { archetype: { Caregiver: 0.5, Connector: 0.5 } } },
  { id: "loop",    label: "Loop in the manager — this isn't mine to fix",      values: { archetype: { Leader: 0.7, Organizer: 0.3 } } },
  { id: "focus",   label: "Focus on my own work — they'll sort it or they won't", values: { archetype: {} } },
];

export function ScenarioTeammateScreen({ onNext }: { onNext: () => void }) {
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<{ archetype: Partial<Record<Archetype, number>> }>) {
    const next = addSignal(tally, opt.values.archetype, 0.7);
    setTally(next);
    setPart2("p2_scenario_teammate", opt.id);
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_scenario_teammate");
  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_scenario_teammate" eyebrow="Something everyone can see" title="Someone on your team is quietly falling behind." />
      <ScenarioChoice
        situation={c.t("situation", "It's obvious. Nobody's saying it out loud. What do you actually do first?")}
        options={STRUGGLE_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }))}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 22 · W-BLANK-001 — blank work week (Scenario → archetype)
// -----------------------------------------------------------------------

const BLANK_OPTIONS: ScenarioOption<{ archetype: Partial<Record<Archetype, number>> }>[] = [
  { id: "make",   label: "Making something new",                       values: { archetype: { Creator: 1 } } },
  { id: "crack",  label: "Cracking a problem I keep circling",         values: { archetype: { Thinker: 1 } } },
  { id: "meet",   label: "Meeting people who do what I want to do",    values: { archetype: { Connector: 1 } } },
  { id: "order",  label: "Getting my life in order — plans, systems, admin", values: { archetype: { Organizer: 1 } } },
];

export function ScenarioOpenWeekScreen({ onNext }: { onNext: () => void }) {
  const [tally, setTally] = useTally();
  const { setPart2 } = useAssessmentV2();
  function pick(opt: ScenarioOption<{ archetype: Partial<Record<Archetype, number>> }>) {
    const next = addSignal(tally, opt.values.archetype, 0.8);
    setTally(next);
    setPart2("p2_scenario_open_week", opt.id);
    setTimeout(onNext, 260);
  }
  const c = useScreenCopy("p2_scenario_open_week");
  return (
    <div className="max-w-2xl mx-auto">
      <Head screenId="p2_scenario_open_week" eyebrow="A blank week" title="A completely open work week. No obligations." />
      <ScenarioChoice
        situation={c.t("situation", "What actually fills it? First instinct — not the wise-sounding answer.")}
        options={BLANK_OPTIONS.map((o) => ({ ...o, label: c.opt(o.id, o.label) }))}
        onPick={pick}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 23 · W-CREATE-001 — creative room (Slider → prefCreativity)
// -----------------------------------------------------------------------

export function CreativeRoomScreen({ onNext }: { onNext: () => void }) {
  const [slots, setSlots] = useSlots();
  const { setPart2 } = useAssessmentV2();
  function commit(v: number) {
    const next = writeSlot(slots, "prefCreativity", "W-CREATE-001", v, {
      label: "Instructions vs blank page", disagreeThreshold: 40,
    });
    setSlots(next);
    setPart2("p2_creative_room", v);
    setTimeout(onNext, 240);
  }
  const c = useScreenCopy("p2_creative_room");
  return (
    <div className="max-w-xl mx-auto">
      <Head
        screenId="p2_creative_room"
        eyebrow="How much runway"
        title="Clear instructions, or a blank page?"
        subtitle="Where do you actually do your best work?"
      />
      <Slider
        leftLabel={c.t("leftLabel", "Give me clear instructions")}
        rightLabel={c.t("rightLabel", "Give me a blank page")}
        onCommit={commit}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// 24 · W-CONFIRM-001 — review
// -----------------------------------------------------------------------

import { ReviewScreen, type ReviewBullet } from "../components/interactions/ReviewScreen";
import type { SlotKey } from "../slots";
import { useNavigate } from "react-router-dom";

const SLOT_LABELS: Partial<Record<SlotKey, string>> = {
  prefOutdoor: "Outdoor vs indoor",
  prefMovement: "On the move vs seated",
  prefScreenTime: "Screen-time comfort",
  prefTravel: "Travel appetite",
  prefStableSchedule: "Schedule stability",
  prefAutonomy: "Autonomy",
  prefImpactVisibility: "Seeing your impact",
  prefIncomeUpside: "Income upside",
  prefPrestige: "Prestige",
  prefStability: "Stability",
  prefCreativity: "Creative runway",
  prefFocusVsSwitching: "Focus vs switching",
  prefSoloVsTeam: "Solo vs team",
  tolPublicSpeaking: "Public speaking",
  tolPeopleContact: "People contact",
  tolClosePhysical: "Close physical work",
  tolSensory: "Sensory exposure",
  tolSocialLoad: "Social load",
  tolEmotionalLoad: "Emotional load",
  tolGruntWork: "Grunt-work tolerance",
};

function bulletFor(key: SlotKey, v: number, lowLabel: string, highLabel: string, midLabel?: string): ReviewBullet {
  if (v <= 30) return { slotKey: key, text: lowLabel };
  if (v >= 70) return { slotKey: key, text: highLabel };
  return { slotKey: key, text: midLabel ?? `${SLOT_LABELS[key] ?? key} — somewhere in the middle.` };
}

export function ReviewConfirmScreen() {
  const [slots] = useSlots();
  const navigate = useNavigate();
  const { state, band } = useAssessmentV2();
  // Screens the learner actually answered, for the session's item count.
  const answeredCount = Object.keys(state.part2).filter((k) => k.startsWith("p2_")).length
    + Object.keys(state.part1).length;
  const v = slots.values;


  const bullets: ReviewBullet[] = [];
  if (v.prefOutdoor != null)
    bullets.push(bulletFor("prefOutdoor", v.prefOutdoor,
      "You'd rather be indoors.", "You'd rather be outdoors or on the move."));
  if (v.prefMovement != null)
    bullets.push(bulletFor("prefMovement", v.prefMovement,
      "You're fine staying seated.", "You want to be moving, not at a desk."));
  if (v.prefScreenTime != null)
    bullets.push(bulletFor("prefScreenTime", v.prefScreenTime,
      "You'd rather not spend the day on a screen.", "You're comfortable with a lot of screen time."));
  if (v.prefStableSchedule != null)
    bullets.push(bulletFor("prefStableSchedule", v.prefStableSchedule,
      "You'll work when the work needs you.", "You want a predictable working week."));
  if (v.prefAutonomy != null)
    bullets.push(bulletFor("prefAutonomy", v.prefAutonomy,
      "You'd rather have someone steering the ship.", "You want to run your own work."));
  if (v.prefIncomeUpside != null)
    bullets.push(bulletFor("prefIncomeUpside", v.prefIncomeUpside,
      "Money isn't what's driving this.", "Income upside matters to you."));
  if (v.prefStability != null)
    bullets.push(bulletFor("prefStability", v.prefStability,
      "You'll take risk for the right work.", "You want stability."));
  if (v.prefCreativity != null)
    bullets.push(bulletFor("prefCreativity", v.prefCreativity,
      "You do your best work with clear instructions.", "You want a blank page."));
  if (v.tolSocialLoad != null)
    bullets.push(bulletFor("tolSocialLoad", v.tolSocialLoad,
      "A day of constant people contact drains you.", "You come out of a people-heavy day charged up."));
  if (v.tolPublicSpeaking != null)
    bullets.push(bulletFor("tolPublicSpeaking", v.tolPublicSpeaking,
      "You'd rather not stand up to a room.", "You're comfortable presenting to groups."));
  if (v.tolEmotionalLoad != null)
    bullets.push(bulletFor("tolEmotionalLoad", v.tolEmotionalLoad,
      "You need work that doesn't follow you home.", "You can carry heavy days without burning out."));

  return (
    <ReviewScreen
      slots={slots}
      bullets={bullets}
      slotLabels={SLOT_LABELS}
      onConfirm={() => {
        // Close out the run in the background. We never hold a learner on
        // this screen waiting for a network call — the result is computed
        // on-device and the canonical report catches up.
        void finishRun(band, answeredCount, state);
        // The dashboard is the home the learner returns to. It carries the
        // banner that takes them straight into the results they just earned.
        navigate(`/dashboard?completed=1&band=${bandParam(band)}`);
      }}
    />
  );
}


// -----------------------------------------------------------------------
// Re-exports so AssessmentV2 can import from one place.
// -----------------------------------------------------------------------

/** Every screen this file exposes, in spec order 5..24. */
export const PART2_BUILT_SCREENS = [
  "p2_workday_sort",       // 5
  "p2_two_offers",         // 6
  "p2_trade_offs",         // 6.5
  "p2_quick_ones",         // 7
  "p2_last_month",         // 7.5
  "p2_grind_test",         // 8
  "p2_collapse",           // 8.5
  "p2_bad_day",            // 9
  "p2_whose_problem",      // 10
  "p2_room",               // 11
  "p2_focus_slider",       // 12
  "p2_people_battery",     // 13
  "p2_hard_day",           // 14
  "p2_working_pattern",    // 15
  "p2_presenting",         // 16
  "p2_ten_years",          // 17
  "p2_scenario_deadline",  // 18
  "p2_scenario_month_off", // 19
  "p2_scenario_failure",   // 20
  "p2_scenario_teammate",  // 21
  "p2_scenario_open_week", // 22
  "p2_creative_room",      // 23
  "p2_here_is_what",       // 24 (review)
] as const;

// Silence unused imports in the closed-loop cases we did not need.
void useMemo;
